import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { ResumoIAMensal } from "@/components/financeiro/ResumoIAMensal";
import { 
  TrendingUp, 
  TrendingDown,
  Wallet,
  ArrowLeftRight,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle
} from "lucide-react";

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr + "T00:00:00").toLocaleDateString('pt-BR');
}

export default function FluxoCaixaPage() {
  const [loading, setLoading] = useState(true);
  const [dataInicio, setDataInicio] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [dataFim, setDataFim] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 2);
    date.setDate(0);
    return date.toISOString().split('T')[0];
  });
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchFluxoCaixa();
  }, [dataInicio, dataFim]);

  const fetchFluxoCaixa = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/financeiro/fluxo-caixa`, {
        params: { data_inicio: dataInicio, data_fim: dataFim }
      });
      setData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar fluxo de caixa");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="fluxo-caixa-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Fluxo de Caixa</h1>
            <p className="text-slate-500">Controle de entradas e saídas - LuvCorp</p>
          </div>
        </div>

        {/* Parecer IA do mês */}
        <ResumoIAMensal />

        {/* Filtro de Período */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-luvcorp-navy" />
                <span className="text-sm font-medium text-slate-700">Período:</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="data-inicio" className="text-sm text-slate-500 whitespace-nowrap">De</Label>
                  <Input
                    id="data-inicio"
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="w-[150px]"
                    data-testid="fluxo-data-inicio"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="data-fim" className="text-sm text-slate-500 whitespace-nowrap">até</Label>
                  <Input
                    id="data-fim"
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="w-[150px]"
                    data-testid="fluxo-data-fim"
                  />
                </div>
              </div>
              <div className="flex gap-2 ml-auto">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const hoje = new Date();
                    setDataInicio(new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0]);
                    setDataFim(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0]);
                  }}
                >
                  Mês Atual
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const hoje = new Date();
                    setDataInicio(new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0]);
                    setDataFim(new Date(hoje.getFullYear(), hoje.getMonth() + 3, 0).toISOString().split('T')[0]);
                  }}
                >
                  Próx. 3 Meses
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const hoje = new Date();
                    setDataInicio(new Date(hoje.getFullYear(), 0, 1).toISOString().split('T')[0]);
                    setDataFim(new Date(hoje.getFullYear(), 11, 31).toISOString().split('T')[0]);
                  }}
                >
                  Ano Atual
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center">
                  <Wallet className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Saldo Inicial</p>
                  <p className="text-xl font-bold text-luvcorp-navy">{formatCurrency(data?.saldo_inicial || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                  <ArrowUpRight className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Entradas</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(data?.entradas_realizadas || 0)}</p>
                  <p className="text-xs text-slate-400">Previstas: {formatCurrency(data?.entradas_previstas || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                  <ArrowDownRight className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Saídas</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(data?.saidas_realizadas || 0)}</p>
                  <p className="text-xs text-slate-400">Previstas: {formatCurrency(data?.saidas_previstas || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-0 shadow-sm border-l-4 ${(data?.saldo_projetado || 0) >= 0 ? 'border-l-blue-500' : 'border-l-orange-500'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${(data?.saldo_projetado || 0) >= 0 ? 'bg-blue-100' : 'bg-orange-100'}`}>
                  <TrendingUp className={`h-5 w-5 ${(data?.saldo_projetado || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Saldo Projetado</p>
                  <p className={`text-xl font-bold ${(data?.saldo_projetado || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                    {formatCurrency(data?.saldo_projetado || 0)}
                  </p>
                  <p className="text-xs text-slate-400">Atual: {formatCurrency(data?.saldo_atual || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cash Flow Chart */}
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowLeftRight className="h-5 w-5 text-luvcorp-gold" />
                Fluxo Diário
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.fluxo_diario?.length > 0 ? (
                <div className="space-y-2">
                  <div className="max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white">
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead className="text-right">Entradas</TableHead>
                          <TableHead className="text-right">Saídas</TableHead>
                          <TableHead className="text-right">Saldo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.fluxo_diario.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{formatDate(item.data)}</TableCell>
                            <TableCell className="text-right text-green-600">
                              {item.entradas > 0 && formatCurrency(item.entradas)}
                            </TableCell>
                            <TableCell className="text-right text-red-600">
                              {item.saidas > 0 && `(${formatCurrency(item.saidas)})`}
                            </TableCell>
                            <TableCell className={`text-right font-medium ${item.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                              {formatCurrency(item.saldo)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Visual Chart */}
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-slate-500 mb-3">Evolução do Saldo</p>
                    <div className="flex items-end gap-1 h-32">
                      {data.fluxo_diario.slice(-30).map((item, index) => {
                        const maxSaldo = Math.max(...data.fluxo_diario.map(i => Math.abs(i.saldo)));
                        const height = maxSaldo > 0 ? (Math.abs(item.saldo) / maxSaldo) * 100 : 0;
                        
                        return (
                          <div 
                            key={index} 
                            className={`flex-1 rounded-t transition-all ${item.saldo >= 0 ? 'bg-blue-400' : 'bg-orange-400'}`}
                            style={{ height: `${Math.max(height, 5)}%` }}
                            title={`${formatDate(item.data)}: ${formatCurrency(item.saldo)}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <ArrowLeftRight className="h-16 w-16 mx-auto mb-4" />
                  <p>Nenhum movimento no período</p>
                  <p className="text-sm mt-2">Cadastre lançamentos financeiros para visualizar o fluxo</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Payments/Receipts */}
          <div className="space-y-6">
            {/* Próximos Recebimentos */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-green-700">
                  <ArrowUpRight className="h-4 w-4" />
                  Próximos Recebimentos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data?.proximos_recebimentos?.length > 0 ? (
                  <div className="space-y-2">
                    {data.proximos_recebimentos.slice(0, 5).map((item, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-slate-700 line-clamp-1">{item.descricao}</p>
                          <p className="text-xs text-slate-400">{formatDate(item.data_vencimento || item.data)}</p>
                        </div>
                        <Badge className="bg-green-100 text-green-700 whitespace-nowrap">
                          {formatCurrency(item.valor)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">Nenhum recebimento previsto</p>
                )}
              </CardContent>
            </Card>

            {/* Próximos Pagamentos */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <ArrowDownRight className="h-4 w-4" />
                  Próximos Pagamentos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data?.proximos_pagamentos?.length > 0 ? (
                  <div className="space-y-2">
                    {data.proximos_pagamentos.slice(0, 5).map((item, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-slate-700 line-clamp-1">{item.descricao}</p>
                          <p className="text-xs text-slate-400">{formatDate(item.data_vencimento || item.data)}</p>
                        </div>
                        <Badge className="bg-red-100 text-red-700 whitespace-nowrap">
                          {formatCurrency(item.valor)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">Nenhum pagamento previsto</p>
                )}
              </CardContent>
            </Card>

            {/* Alert if negative projected */}
            {(data?.saldo_projetado || 0) < 0 && (
              <Card className="border-0 shadow-sm bg-orange-50 border border-orange-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-orange-800">Atenção!</p>
                      <p className="text-sm text-orange-700 mt-1">
                        O saldo projetado está negativo. Revise os pagamentos previstos ou antecipe recebimentos.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
